import { createAsyncThunk } from '@reduxjs/toolkit';
import { createEntity, fetchDataById } from '../../../services/axiosInstanceService';
import { modalsActions } from '../../modals/modalsSlice';
import { subscriptionParser } from '../subscriptionParser';

/**
 * Async thunk to fetch a subscription by its ID IRI.
 *
 * This function retrieves subscription data from the API using the provided subscription ID IRI.
 * If the ID is missing, an error is thrown. The response is then parsed before being returned.
 *
 * @param {Object} params - The parameters required to fetch the subscription.
 * @param {string | null} params.subscriptionIdIri - The unique IRI identifier of the subscription.
 * @returns {Promise<ISubscription>} The parsed subscription data.
 * @throws {Error} Throws an error if `subscriptionIdIri` is null or invalid.
 */
const fetchSubscriptionThunk = createAsyncThunk(
  'subscription/fetchSubscriptionThunk',
  async (params: { subscriptionIdIri: string | null }) => {
    try {
      // Validate that the subscription ID IRI is provided
      if (!params.subscriptionIdIri) throw new Error('Subscription idIri not found');

      // Fetch subscription data from the API
      const result = await fetchDataById(params.subscriptionIdIri);

      // Parse the API response into the expected format
      const parsed = subscriptionParser(result);

      // Return the parsed subscription data
      return parsed;
    } catch (error) {
      // Reject the promise with the caught error
      return Promise.reject(error);
    }
  },
);

/**
 * Async thunk to request a subscription update.
 *
 * This function sends a request to create a new subscription update demand.
 * It validates the subscription ID IRI before proceeding and dispatches an action
 * to open the subscription summary modal upon success.
 *
 * @param {SubscriptionUpdateFormType & { subscriptionIdIri: string | null }} params - The request parameters.
 * @param {string | null} params.subscriptionIdIri - The unique IRI identifier of the subscription.
 * @param {string} [params.title='others'] - The title of the update request.
 * @param {string} [params.comment=''] - Additional comments for the update request.
 * @param {Function} dispatch - Redux dispatch function.
 * @returns {Promise<string>} The newly created subscription demand ID.
 * @throws {Error} Throws an error if `subscriptionIdIri` is missing or the API request fails.
 */
const askForSubscriptionUpdateThunk = createAsyncThunk(
  'subscription/askForSubscriptionUpdateThunk',
  async (
    params: SubscriptionUpdateFormType & { subscriptionIdIri: string | null },
    { dispatch },
  ) => {
    try {
      // Validate that the subscription ID IRI is provided
      if (!params.subscriptionIdIri) throw new Error('No subscription idIri found');

      // Create a new subscription update demand
      const result = await createEntity({
        endpoint: '/subscription-demands',
        body: {
          title: params.title ?? 'others', // Default title if not provided
          comment: params.comment ?? '', // Default comment if not provided
          subscription: params.subscriptionIdIri, // Reference to the subscription
        },
      });

      // Dispatch an action to open the Subscription Summary modal
      dispatch(modalsActions.subscriptionUpdateSummary(params));

      // Return the newly created subscription demand ID
      return result['@id'];
    } catch (error) {
      // Reject the promise with the caught error
      return Promise.reject(error);
    }
  },
);

export { askForSubscriptionUpdateThunk, fetchSubscriptionThunk };
